import React from 'react';
import Spinner from './Spinner';

/**
 * v2 Button — cobalt = user actions. Depth from 1px borders, never shadows.
 * Violet is Robbie-only: there is deliberately NO violet button variant.
 * `loading` swaps the content for the shared spinner + a verb ("Saving…")
 * while keeping the button's width, so nothing around it moves.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
  /** Busy: disabled, spinner + `loadingLabel`, same width as at rest. */
  loading?: boolean;
  /** The verb shown while busy ("Saving…"); defaults to the button's own label. */
  loadingLabel?: React.ReactNode;
}

const base =
  'inline-flex items-center justify-center gap-s2 font-medium select-none ' +
  'rounded-control border transition-colors duration-100 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-1 ' +
  'disabled:pointer-events-none';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-action border-action text-white hover:bg-action-hover hover:border-action-hover',
  secondary: 'bg-surface border-line text-ink hover:bg-surface-hover hover:border-line-strong',
  ghost: 'bg-transparent border-transparent text-ink-secondary hover:bg-neutral-bg hover:text-ink',
  danger: 'bg-danger-ink border-danger-ink text-white hover:opacity-90',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-7 px-s3 text-body-sm',
  md: 'h-8 px-s4 text-body-md',
  lg: 'h-10 px-s6 text-row-title',
};

const iconWrap = 'inline-flex shrink-0 [&>svg]:h-[13px] [&>svg]:w-[13px]';
const stackCell = 'col-start-1 row-start-1 inline-flex items-center justify-center gap-s2';

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary', size = 'md', iconLeft, iconRight, fullWidth, loading = false, loadingLabel,
      className = '', children, type = 'button', disabled, ...rest
    },
    ref
  ) => {
    const content = (
      <>
        {iconLeft && <span className={iconWrap}>{iconLeft}</span>}
        {children}
        {iconRight && <span className={iconWrap}>{iconRight}</span>}
      </>
    );
    const filled = variant === 'primary' || variant === 'danger';
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={`${base} ${variants[variant]} ${sizes[size]} ${fullWidth ? 'w-full' : ''} ${
          loading ? 'cursor-progress' : 'disabled:opacity-50'
        } ${className}`}
        {...rest}
      >
        {loading ? (
          <span className="inline-grid">
            <span aria-hidden="true" className={`${stackCell} invisible`}>{content}</span>
            <span className={stackCell}>
              <Spinner tone={filled ? 'white' : 'action'} />
              {loadingLabel ?? children}
            </span>
          </span>
        ) : (
          content
        )}
      </button>
    );
  }
);
Button.displayName = 'Button';

export default Button;
